import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../../contexts/authContext';
import { projectDataClient } from '../connect/clients';
import { documentsQuery, mentionHref, programsQuery, pursuitsQuery } from './mentions';
const MAX = 6;
/** A mention link whose label can't break the markdown around it. */ function mention(kind, id, name) {
    const label = name.replace(/[[\]]/g, (c)=>c === '[' ? '(' : ')').trim();
    return `[@${label}](${mentionHref(kind, id)})`;
}
export function buildStarters(r) {
    const out = [];
    if (r.program) {
        out.push({
            id: 'program-due',
            prompt: `What's due next on ${mention('program', r.program.id, r.program.name)}, and who owns it?`
        });
    }
    if (r.document) {
        out.push({
            id: 'document-summary',
            prompt: `Summarize the key points of ${mention('document', r.document.id, r.document.name)}`
        });
    }
    if (r.pursuit) {
        out.push({
            id: 'pursuit-next',
            prompt: `Summarize ${mention('pursuit', r.pursuit.id, r.pursuit.name)} and what we should do next`
        });
    }
    if (r.template) {
        out.push({
            id: 'template-start',
            prompt: `Start a new document from ${mention('library', r.template.id, r.template.name)} and fill it in with me`
        });
    }
    if (r.program) {
        out.push({
            id: 'attention',
            prompt: 'What needs my attention this week?'
        });
    }
    if (!r.program && !r.pursuit) {
        out.push({
            id: 'first-program',
            prompt: 'How do I add my first program or pursuit?'
        });
    }
    out.push({
        id: 'remember',
        prompt: 'Remember that I prefer short answers with bullet points'
    });
    out.push({
        id: 'help',
        prompt: 'What can you help me with in this app?'
    });
    return out.slice(0, MAX);
}
/** The starters for the signed-in user, from the same cached lists the @ menu reads. */ export function useStarterPrompts() {
    const { user } = useAuth();
    const tenantId = user?.tenant_id ?? '';
    const userId = user?.id ?? '';
    const programs = useQuery(programsQuery(tenantId, '', true));
    const pursuits = useQuery(pursuitsQuery(tenantId, true));
    const documents = useQuery(documentsQuery(tenantId, userId, true));
    const templates = useQuery({
        queryKey: [
            'starter-templates',
            tenantId
        ],
        enabled: !!tenantId,
        staleTime: 60000,
        retry: false,
        // Released templates first, then the user's own (My items), which the
        // default discovery search leaves out.
        queryFn: async ()=>{
            for (const scope of [
                'discovery',
                'owner'
            ]){
                const { items } = await projectDataClient.searchLibraryItems({
                    filter: {
                        scope,
                        query: '',
                        itemType: 'template'
                    },
                    offset: 0,
                    limit: 1
                });
                if (items.length > 0) return items;
            }
            return [];
        }
    });
    const p = programs.data?.find((x)=>x.programNameShort || x.programNameFull);
    const c = pursuits.data?.find((x)=>x.name);
    // The most recent document is the one they are most likely working on.
    const d = [
        ...documents.data ?? []
    ].filter((x)=>x.displayTitle || x.filename).sort((a, b)=>Number(b.createdAt?.seconds ?? 0) - Number(a.createdAt?.seconds ?? 0))[0];
    const t = templates.data?.find((x)=>x.title);
    return buildStarters({
        program: p ? {
            id: p.id,
            name: p.programNameShort || p.programNameFull
        } : undefined,
        pursuit: c ? {
            id: c.captureProjectId,
            name: c.name
        } : undefined,
        document: d ? {
            id: d.artifactId,
            name: d.displayTitle || d.filename
        } : undefined,
        template: t ? {
            id: t.id,
            name: t.title
        } : undefined
    });
}
